import {
  ESymbolCategory,
  IAction,
  IActionDefinitionDto,
  IActionParameter,
  IDisplayItem,
  IDisplayItemProperty,
  IKTypeField,
  IScreenDesign,
  IScreenDesignDictionary,
  ISymbol
} from "@kuika/kuika-cl-model";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";
import type { DefaultValueEntry } from "./designer-property-actions";
import { DEFAULT_VALUES } from "./designer-property-actions";
import {
  safeParse,
  getScreenDictionary as getScreenDesignDictionary,
  getScreenDesign,
  getScreenProvider
} from "./designer-utils";
import { PropertySdHelper } from "./property-sd-helper";

/**
 * Recognized `default` symbol names, READ from the platform registry rather
 * than restated here. Both spellings a stored symbol can legally carry are
 * accepted — the persisted `name` and the picker's `label` — because codegen
 * resolves both.
 */
const KNOWN_DEFAULTS: ReadonlySet<string> = new Set(
  DEFAULT_VALUES.flatMap((entry: DefaultValueEntry): string[] => [entry.name, entry.label])
);
const INTERNAL_PARAMS = new Set(["executePageInitActionsAfterClose"]);

interface ValidationIssue {
  category: string;
  severity: "error" | "warning" | "info";
  issue: string;
  components: string[];
  details?: Record<string, unknown>;
  suggestion?: string;
}

interface ValidationResult {
  success: true;
  score: number;
  componentCount: number;
  summary: string;
  issues: ValidationIssue[];
}

interface ValidationError {
  error: string;
}

function getActionDefinitions(sd: IScreenDesignContextState): IActionDefinitionDto[] {
  return sd?.actionDefinitions || [];
}

function collectAllActionInstances(displayItems: IDisplayItem[]): { action: IAction; componentKuikaId: string }[] {
  const results: { action: IAction; componentKuikaId: string }[] = [];

  function walkActions(actions: IAction[], diId: string) {
    for (const action of actions) {
      results.push({ action, componentKuikaId: diId });
      if (action.ifThenElseData) {
        walkActions(action.ifThenElseData.ifActions || [], diId);
        walkActions(action.ifThenElseData.elseActions || [], diId);
      }
    }
  }

  function walkDi(di: IDisplayItem) {
    const events = di.item?.events || [];
    for (const event of events) {
      walkActions(event.actions || [], di.id?.toString());
    }
  }

  for (const di of displayItems) {
    walkDi(di);
  }

  return results;
}

function collectActionIds(action: IAction, enabledIds: Set<string>, disabledIds: Set<string>) {
  if (action.id) {
    if (action.enabled === false) {
      disabledIds.add(action.id);
    } else {
      enabledIds.add(action.id);
    }
  }
  if (action.ifThenElseData) {
    for (const branchAction of action.ifThenElseData.ifActions || [])
      collectActionIds(branchAction, enabledIds, disabledIds);
    for (const branchAction of action.ifThenElseData.elseActions || [])
      collectActionIds(branchAction, enabledIds, disabledIds);
  }
}

function checkBindings(dict: IScreenDesignDictionary, sd: IScreenDesignContextState): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const screenInputs = getScreenProvider(sd)?.getScreenInputs?.() || [];
  const screenInputNames = new Set(screenInputs.map((screenInput) => screenInput.name));

  const allDisplayItems = Object.values(dict) as IDisplayItem[];
  const actionInstanceIds = new Set<string>();
  const disabledActionInstanceIds = new Set<string>();
  for (const di of allDisplayItems) {
    const events = di.item?.events || [];
    for (const event of events) {
      for (const action of event.actions || []) {
        collectActionIds(action, actionInstanceIds, disabledActionInstanceIds);
      }
    }
  }

  for (const di of allDisplayItems) {
    const properties = di.item?.properties || [];
    for (const property of properties) {
      const symbol = property.symbol;
      if (
        !symbol ||
        symbol.category == null ||
        symbol.category === ESymbolCategory.none ||
        symbol.category === ESymbolCategory.fixed
      ) {
        continue;
      }

      const kuikaId = di.id?.toString();
      switch (symbol.category) {
        case ESymbolCategory.screenInput:
          if (symbol.inputName && !screenInputNames.has(symbol.inputName)) {
            issues.push({
              category: "bindings",
              severity: "error",
              issue: "invalid_screen_input_binding",
              components: [kuikaId],
              details: { property: property.name, inputName: symbol.inputName },
              suggestion: `Property "${property.name}" is bound to screen input "${symbol.inputName}" which does not exist. Create the screen input or rebind.`
            });
          }
          break;
        case ESymbolCategory.displayItemProp: {
          const targetDiId = symbol.diId;
          if (targetDiId != null && !dict[targetDiId]) {
            issues.push({
              category: "bindings",
              severity: "error",
              issue: "invalid_component_binding",
              components: [kuikaId],
              details: { property: property.name, targetDiId, targetProperty: symbol.diPropName },
              suggestion: `Property "${property.name}" is bound to component ${targetDiId} which no longer exists on screen.`
            });
          } else if (targetDiId != null && dict[targetDiId]) {
            const targetProperties = dict[targetDiId].item?.properties || [];
            if (
              symbol.diPropName &&
              !targetProperties.some(
                (targetProperty: IDisplayItemProperty) => targetProperty.name === symbol.diPropName
              )
            ) {
              issues.push({
                category: "bindings",
                severity: "warning",
                issue: "invalid_component_property_binding",
                components: [kuikaId],
                details: { property: property.name, targetDiId, targetProperty: symbol.diPropName },
                suggestion: `Property "${property.name}" is bound to property "${symbol.diPropName}" on component ${targetDiId}, but that property was not found.`
              });
            }
          }
          break;
        }
        case ESymbolCategory.actionResult:
          if (symbol.resultActionId && !actionInstanceIds.has(symbol.resultActionId)) {
            const isDisabled: boolean = disabledActionInstanceIds.has(symbol.resultActionId);
            issues.push({
              category: "bindings",
              severity: "error",
              issue: "invalid_action_result_binding",
              components: [kuikaId],
              details: {
                property: property.name,
                actionId: symbol.resultActionId,
                reason: isDisabled ? "disabled" : "missing"
              },
              suggestion: isDisabled
                ? `Property "${property.name}" is bound to the result of action "${symbol.resultActionId}" which is currently disabled. Re-enable the action or remove the binding.`
                : `Property "${property.name}" is bound to the result of action "${symbol.resultActionId}" which is not found on screen.`
            });
          }
          break;
        case ESymbolCategory.actionField:
          if (symbol.parentActionId && !actionInstanceIds.has(symbol.parentActionId)) {
            const isDisabled: boolean = disabledActionInstanceIds.has(symbol.parentActionId);
            issues.push({
              category: "bindings",
              severity: "error",
              issue: "invalid_action_field_binding",
              components: [kuikaId],
              details: {
                property: property.name,
                parentActionId: symbol.parentActionId,
                reason: isDisabled ? "disabled" : "missing"
              },
              suggestion: isDisabled
                ? `Property "${property.name}" is bound to a field of action "${symbol.parentActionId}" which is currently disabled. Re-enable the action or remove the binding.`
                : `Property "${property.name}" is bound to a field of action "${symbol.parentActionId}" which is not found on screen.`
            });
          }
          break;
        case ESymbolCategory.default:
          if (symbol.defaultValueName && !KNOWN_DEFAULTS.has(symbol.defaultValueName)) {
            issues.push({
              category: "bindings",
              severity: "warning",
              issue: "unknown_default_binding",
              components: [kuikaId],
              details: { property: property.name, defaultValueName: symbol.defaultValueName },
              suggestion: `Property "${property.name}" is bound to default "${symbol.defaultValueName}" which is not a recognized default value.`
            });
          }
          break;
        case ESymbolCategory.bag:
          if (!symbol.bagName) {
            issues.push({
              category: "bindings",
              severity: "warning",
              issue: "empty_bag_binding",
              components: [kuikaId],
              details: { property: property.name },
              suggestion: `Property "${property.name}" is bound to a bag variable but the bag name is empty.`
            });
          }
          break;
      }
    }
  }

  // Defensive: the data-field check resolves action schemas through PropertySdHelper;
  // if any resolver throws on a malformed symbol it must not sink the whole validation
  // pass — skip just this check and still return the other binding issues. (Zero cost on
  // the success path; only the rare throw pays for unwinding.)
  let dataFieldIssues: ValidationIssue[] = [];
  try {
    dataFieldIssues = checkDataFieldBindings(dict, sd);
  } catch (error) {
    console.error("checkDataFieldBindings failed; skipping orphaned-column check:", error);
  }
  return [...issues, ...dataFieldIssues];
}

/**
 * Detects table/grid column-cell bindings (an `actionField` symbol whose `fieldName` is a
 * datasource column) that reference a column which no longer exists — e.g. a Table bound to
 * a SQL action keeps a column bound after that column is removed from the query. The category
 * checks above only verify the bound action *instance* exists; they never check that the
 * referenced *column* is still in the action's return schema, so these orphaned column
 * bindings passed validation silently. Scope is deliberately limited to the table-column case
 * (matching the backend's Warning downgrade); chart data-fields, map coordinates and selectbox
 * keys are left to the backend validator, which reports those as errors.
 *
 * A binding is flagged only when the source action and its live return schema are both
 * resolvable — the valid column set is computed with the same resolver the column picker
 * uses (`getActionFieldsBySymbol` + dynamic-interface columns), so anything the user could
 * legitimately have picked is accepted. If the schema cannot be introspected the binding is
 * left alone: we never flag what we cannot prove is missing.
 */
function checkDataFieldBindings(dict: IScreenDesignDictionary, sd: IScreenDesignContextState): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const actionDefinitions: IActionDefinitionDto[] = getActionDefinitions(sd);
  if (actionDefinitions.length === 0) return issues;

  const propertyHelper: PropertySdHelper = new PropertySdHelper(() => sd);
  const columnNameCache: Map<string, Set<string> | null> = new Map();

  function resolveValidColumnNames(sourceSymbol: ISymbol): Set<string> | null {
    const actionId: string | undefined = sourceSymbol.actionDefinitionId;
    if (!actionId) return null;
    const cacheKey = `${actionId}::${sourceSymbol.actionDefinitionObjectPath ?? ""}`;
    const cached: Set<string> | null | undefined = columnNameCache.get(cacheKey);
    if (cached !== undefined) return cached;

    // Only validate against a live, introspected schema; otherwise we cannot prove a
    // column is gone (action deleted, or return columns not resolvable for this type).
    const actionDefinition: IActionDefinitionDto | undefined = actionDefinitions.find(
      (definition) => definition.id === actionId
    );
    if (!actionDefinition) {
      columnNameCache.set(cacheKey, null);
      return null;
    }

    const names: Set<string> = new Set<string>();
    const fields: IKTypeField[] = propertyHelper.getActionFieldsBySymbol(sourceSymbol, true) || [];
    for (const field of fields) {
      if (typeof field?.name === "string") names.add(field.name);
    }
    // Mirror the column picker exactly: dynamic-interface columns are selectable too.
    const dynamicAction: IAction | undefined = propertyHelper.getActionDynamicFields(actionId);
    const dynamicElements: IKTypeField[] = dynamicAction?.returnTypeDynamicInterface?.elements || [];
    for (const field of dynamicElements) {
      if (typeof field?.name === "string") names.add(field.name);
    }

    const result: Set<string> | null = names.size > 0 ? names : null;
    columnNameCache.set(cacheKey, result);
    return result;
  }

  const allDisplayItems: IDisplayItem[] = Object.values(dict) as IDisplayItem[];
  for (const di of allDisplayItems) {
    const kuikaId = di.id?.toString();
    const properties = di.item?.properties || [];
    for (const property of properties) {
      const symbol = property.symbol;
      if (!symbol) continue;

      // The column reference may live on the component's own symbol (its own
      // actionDefinitionId) or be inherited from the enclosing table/list/chart options
      // symbol — table column cells carry only fieldName, not the action id.
      const sourceSymbol: ISymbol | undefined = symbol.actionDefinitionId
        ? symbol
        : propertyHelper.getTableOptionPropSymbolByDi(di);
      if (!sourceSymbol?.actionDefinitionId) continue;

      const validColumnNames: Set<string> | null = resolveValidColumnNames(sourceSymbol);
      if (!validColumnNames) continue;

      // Scoped to table/grid column cells: an actionField binding carries the bound column
      // in `fieldName`. Other display bindings (chart data-fields, map lat/long, selectbox
      // keys) are intentionally left to the backend validator, which already reports them.
      const references: { refKind: string; column: string }[] = [];
      if (
        symbol.category === ESymbolCategory.actionField &&
        typeof symbol.fieldName === "string" &&
        symbol.fieldName.length > 0
      ) {
        references.push({ refKind: "fieldName", column: symbol.fieldName });
      }

      for (const reference of references) {
        // Nested object paths resolve against an on-demand sub-model the picker builds;
        // skip them to avoid false positives on deep paths.
        if (reference.column.includes(".")) continue;
        if (validColumnNames.has(reference.column)) continue;

        const actionName: string =
          actionDefinitions.find((definition) => definition.id === sourceSymbol.actionDefinitionId)?.name ||
          sourceSymbol.actionDefinitionId ||
          "";
        issues.push({
          // A stale/orphaned column binding is a genuine defect — reported as an error, mirroring the
          // backend (blocking) and the existing chart / current-row / selectbox field-existence checks.
          category: "bindings",
          severity: "error",
          issue: "invalid_data_field_binding",
          components: [kuikaId],
          details: {
            property: property.name,
            column: reference.column,
            refKind: reference.refKind,
            actionId: sourceSymbol.actionDefinitionId,
            actionName
          },
          suggestion: `Property "${property.name}" is bound to column "${reference.column}" from action "${actionName}", but that column is no longer returned by the action. Re-bind to an existing column or remove the binding.`
        });
      }
    }
  }

  return issues;
}

function checkActionParameters(dict: IScreenDesignDictionary, sd: IScreenDesignContextState): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const actionDefinitions = getActionDefinitions(sd);
  if (!actionDefinitions || actionDefinitions.length === 0) return issues;

  const defMap = new Map<string, IActionDefinitionDto>();
  for (const definition of actionDefinitions) {
    if (definition.id) defMap.set(definition.id, definition);
  }

  const displayItems = Object.values(dict) as IDisplayItem[];
  const actionInstances = collectAllActionInstances(displayItems);

  for (const { action, componentKuikaId } of actionInstances) {
    const definitionId = action.actionDefinitionId;
    if (!definitionId) continue;
    const definition = defMap.get(definitionId);
    if (!definition || !definition.actionParameters || !Array.isArray(definition.actionParameters)) continue;

    const actionParams = action.parameters || [];
    for (const definitionParameter of definition.actionParameters) {
      if (INTERNAL_PARAMS.has(definitionParameter.name)) continue;
      if (definitionParameter.isOptionalParameter) continue;

      const instanceParam = actionParams.find((param: IActionParameter) => param.name === definitionParameter.name);
      if (!instanceParam) continue;

      const symbol = instanceParam.symbol;
      if (!symbol || symbol.category == null || symbol.category === ESymbolCategory.none) {
        issues.push({
          category: "actionParameters",
          severity: "warning",
          issue: "unset_action_parameter",
          components: [componentKuikaId],
          details: {
            actionId: action.id,
            actionLabel: action.label || action.description || "Unnamed",
            parameterName: definitionParameter.name,
            parameterType: definitionParameter.Typename || "unknown"
          },
          suggestion: `Action "${action.label || action.id}" has parameter "${definitionParameter.name}" with no value set. Bind or set a fixed value.`
        });
      }
    }
  }

  return issues;
}

function checkOrphanComponents(dict: IScreenDesignDictionary): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const rootIds = new Set([-1, -2, -3]);

  for (const key of Object.keys(dict)) {
    const di = dict[key as unknown as number];
    const parentId = di.parentId;
    if (parentId == null) continue;
    if (rootIds.has(parentId)) continue;
    if (dict[parentId]) continue;

    issues.push({
      category: "orphans",
      severity: "error",
      issue: "orphaned_component",
      components: [di.id?.toString()],
      details: {
        componentName: di.item?.name || "Unknown",
        friendlyName: di.friendlyName || null,
        missingParentId: parentId
      },
      suggestion: `Component "${di.friendlyName || di.item?.name || key}" (kuikaId: ${key}) references parent ${parentId} which does not exist in the screen dictionary.`
    });
  }

  return issues;
}

function checkCircularReferences(dict: IScreenDesignDictionary): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  for (const key of Object.keys(dict)) {
    const visited = new Set<number>();
    let current: IDisplayItem | undefined = dict[key as unknown as number];
    let hasCycle = false;

    while (current) {
      const id = current.id ?? parseInt(key, 10);
      if (visited.has(id)) {
        hasCycle = true;
        break;
      }
      visited.add(id);
      const parentId = current.parentId;
      if (parentId == null || !dict[parentId]) break;
      current = dict[parentId];
    }

    if (hasCycle) {
      issues.push({
        category: "circularReferences",
        severity: "error",
        issue: "parent_chain_cycle",
        components: [key],
        details: { visitedIds: [...visited].map(String) },
        suggestion: `Component ${key} has a circular parent chain. This will cause infinite loops at runtime.`
      });
    }
  }

  const bindingEdges = new Map<string, Set<string>>();
  for (const key of Object.keys(dict)) {
    const di = dict[key as unknown as number];
    const properties = di.item?.properties || [];
    for (const property of properties) {
      const symbol = property.symbol;
      if (symbol?.category === ESymbolCategory.displayItemProp && symbol.diId != null) {
        const sourceId = key;
        const targetId = symbol.diId.toString();
        if (sourceId !== targetId) {
          if (!bindingEdges.has(sourceId)) bindingEdges.set(sourceId, new Set());
          bindingEdges.get(sourceId)!.add(targetId);
        }
      }
    }
  }

  const WHITE = 0;
  const GRAY = 1;
  const BLACK = 2;
  const color = new Map<string, number>();
  const cycleNodes = new Set<string>();

  function dfs(node: string): boolean {
    color.set(node, GRAY);
    const neighbors = bindingEdges.get(node);
    if (neighbors) {
      for (const next of neighbors) {
        const currentColor = color.get(next) ?? WHITE;
        if (currentColor === GRAY) {
          cycleNodes.add(node);
          cycleNodes.add(next);
          return true;
        }
        if (currentColor === WHITE && dfs(next)) {
          cycleNodes.add(node);
          return true;
        }
      }
    }
    color.set(node, BLACK);
    return false;
  }

  for (const node of bindingEdges.keys()) {
    if ((color.get(node) ?? WHITE) === WHITE) {
      dfs(node);
    }
  }

  if (cycleNodes.size > 0) {
    issues.push({
      category: "circularReferences",
      severity: "error",
      issue: "binding_cycle",
      components: [...cycleNodes],
      details: { involvedComponents: [...cycleNodes] },
      suggestion: `Circular binding detected between components [${[...cycleNodes].join(", ")}]. Component property bindings form a cycle which may cause infinite update loops.`
    });
  }

  return issues;
}

function calculateScore(issues: ValidationIssue[]): number {
  let score = 100;
  for (const issue of issues) {
    switch (issue.severity) {
      case "error":
        score -= 10;
        break;
      case "warning":
        score -= 5;
        break;
      case "info":
        score -= 2;
        break;
    }
  }
  return Math.max(0, Math.min(100, score));
}

export function validateDesignerScreen(
  sdInstance: ScreenDesignInstance,
  input: { scope?: string; categories?: unknown; kuikaId?: string } = {}
): ValidationResult | ValidationError {
  const scope = input.scope || "full";
  const categories = safeParse(input.categories);

  const sd = getValues(sdInstance);
  const dict = getScreenDesignDictionary(sd);

  let rootDi: IDisplayItem | undefined;
  if (scope === "section" && input.kuikaId) {
    rootDi = dict[parseInt(input.kuikaId, 10)];
    if (!rootDi) return { error: `Component "${input.kuikaId}" not found` };
  } else {
    rootDi = dict[-1];
    if (!rootDi) return { error: "Screen design not found" };
  }

  const scopeDisplayItems: IDisplayItem[] = [rootDi];
  if (scope === "full") {
    const screenDesign = getScreenDesign(sd) as IScreenDesign & { header?: IDisplayItem; footer?: IDisplayItem };
    if (screenDesign?.header) scopeDisplayItems.push(screenDesign.header);
    if (screenDesign?.footer) scopeDisplayItems.push(screenDesign.footer);
  }

  let componentCount = 0;
  function countDisplayItem(di: IDisplayItem) {
    componentCount++;
    if (di.children) {
      for (const child of di.children) countDisplayItem(child);
    }
  }
  for (const di of scopeDisplayItems) countDisplayItem(di);

  const allCategories = ["bindings", "actionParameters", "orphans", "circularReferences"];
  const activeCategories =
    categories && Array.isArray(categories) && categories.length > 0 ? categories : allCategories;

  const allIssues: ValidationIssue[] = [];
  for (const category of activeCategories) {
    let result: ValidationIssue[];
    switch (category) {
      case "bindings":
        result = checkBindings(dict, sd);
        break;
      case "actionParameters":
        result = checkActionParameters(dict, sd);
        break;
      case "orphans":
        result = checkOrphanComponents(dict);
        break;
      case "circularReferences":
        result = checkCircularReferences(dict);
        break;
      default:
        continue;
    }
    allIssues.push(...result);
  }

  const score = calculateScore(allIssues);
  const errorCount = allIssues.filter((issue) => issue.severity === "error").length;
  const warningCount = allIssues.filter((issue) => issue.severity === "warning").length;
  const infoCount = allIssues.filter((issue) => issue.severity === "info").length;

  const summaryParts: string[] = [];
  if (errorCount > 0) summaryParts.push(`${errorCount} error${errorCount > 1 ? "s" : ""}`);
  if (warningCount > 0) summaryParts.push(`${warningCount} warning${warningCount > 1 ? "s" : ""}`);
  if (infoCount > 0) summaryParts.push(`${infoCount} suggestion${infoCount > 1 ? "s" : ""}`);

  return {
    success: true,
    score,
    componentCount,
    summary:
      allIssues.length === 0
        ? "No structural issues found. Screen is valid!"
        : `Found ${summaryParts.join(", ")} across ${componentCount} components.`,
    issues: allIssues
  };
}
